import { useState, useCallback, ReactElement } from 'react';
import {
  type ValidationRule as FLCValidationRule,
  type EditableField,
} from '@brightmls/nextgen.lae.flc';
import {
  FieldMetadata,
  useMetadata,
} from '../../../../contexts/MetadataContext';
import { useSidePanel } from '../../../../contexts/SidePanelContext';
import EditableFieldForm from './EditableFieldForm';
import { SxProps } from '@mui/material';
import { MemoizedFieldContextProps } from '../../../../pages/edit/components/MemoizedField';

export type ValidationRule = FLCValidationRule;

export interface ResourceField extends EditableField {
  Key: string;
  Label?: string;
  Layout?: string;
  Control?: string;
  Metadata?: Partial<FieldMetadata>;
  passEditableFieldFormContext?: boolean;
  SkipRequiredIfReadOnly?: boolean;
  SkipRequired?: boolean;
  ForceRequired?: boolean;
  ForceNeverDisabled?: boolean;
  onFieldChange?: (value: any, context: MemoizedFieldContextProps) => void;
}

interface EditableFieldFormConfig {
  fields: ResourceField[];
  formHeader?: ReactElement;
  formStyles?: SxProps;
}

interface EditableFieldFormDisplayConfig {
  title: string;
  width?: number;
}

export const useEditableFieldForm = () => {
  const { field: getFieldMetadata } = useMetadata();
  const { showPanel, hidePanel } = useSidePanel();
  const [isOpen, setIsOpen] = useState(false);

  const openEditableFieldForm = useCallback(
    (
      formConfig: EditableFieldFormConfig,
      displayConfig: EditableFieldFormDisplayConfig,
      saveHook: (resource: any) => any | false,
      initialValues?: any,
    ) => {
      const handleSave = async (values: any) => {
        let processedValues = values;

        processedValues = await Promise.resolve(saveHook(values));
        if (processedValues === false) {
          // Save hook returned false, just close without saving
          hidePanel();
          setIsOpen(false);
          return;
        }

        hidePanel();
        setIsOpen(false);
      };

      setIsOpen(true);
      showPanel(
        displayConfig.title,
        <EditableFieldForm
          formHeader={formConfig.formHeader}
          formStyles={formConfig.formStyles}
          fields={formConfig.fields}
          initialValues={initialValues}
          onSave={handleSave}
          onCancel={() => {
            hidePanel();
            setIsOpen(false);
          }}
          getBaseFieldMetadata={getFieldMetadata}
        />,
        undefined,
        displayConfig.width,
      );
    },
    [showPanel, getFieldMetadata, hidePanel],
  );

  return {
    openEditableFieldForm,
    isOpen,
  };
};
